import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  getOverviewAnalysis,
  isProcessing,
  listOverviewAnalyses,
  type OverviewAnalysis,
  type OverviewAnalysisSummary,
} from '../services/livestockOverviewService';

/** How often a running analysis is polled. */
export const OVERVIEW_POLL_INTERVAL_MS = 2000;

export const livestockOverviewKeys = {
  all: (aquariumId: string) => ['livestock-overview', aquariumId] as const,
  list: (aquariumId: string) => ['livestock-overview', aquariumId, 'list'] as const,
  detail: (aquariumId: string, analysisId: string) => ['livestock-overview', aquariumId, 'detail', analysisId] as const,
};

/** One analysis; polled while it is still detecting/identifying. */
export function useOverviewAnalysis(aquariumId: string, analysisId: string | undefined): UseQueryResult<OverviewAnalysis, Error> {
  return useQuery({
    queryKey: livestockOverviewKeys.detail(aquariumId, analysisId ?? ''),
    queryFn: () => getOverviewAnalysis(aquariumId, analysisId ?? ''),
    enabled: Boolean(aquariumId) && Boolean(analysisId),
    refetchInterval: (query) => (isProcessing(query.state.data?.status) ? OVERVIEW_POLL_INTERVAL_MS : false),
    // The review state lives in the page while the user edits; never overwrite it on focus.
    refetchOnWindowFocus: false,
  });
}

/** The aquarium's recent analyses ("continue a previous analysis"). */
export function useOverviewAnalyses(aquariumId: string, enabled = true): UseQueryResult<OverviewAnalysisSummary[], Error> {
  return useQuery({
    queryKey: livestockOverviewKeys.list(aquariumId),
    queryFn: () => listOverviewAnalyses(aquariumId),
    enabled: Boolean(aquariumId) && enabled,
  });
}
